import * as i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import * as moment from 'moment';
import { fetchLanguage } from './language-provider.utils';
// constants
import { DEFAULT_LANGUAGE } from 'constants/locales';
import { TRANSLATION_NAME_SPACES } from './i18n.constants';
import { ENV } from '@sitegen/common/constants/environments';

// types
import { IPromisedTranslation, ITranslation } from 'types/translation';

interface IChangeLanguage {
    languageCode: string;
    translationsPath: string | null;
    fallbackTranslationsPath: string | null;
    defaultTranslations?: object
}

interface IInitOptions {
    readonly env: ENV;
    readonly initialTranslations: ITranslation;
}

class I18nService {
    private i18nextInstance: i18next.i18n;
    private initialTranslations: ITranslation;
    private fallbackTranslation: ITranslation;
    private useCache: boolean = true;

    public async init(options: IInitOptions): Promise<void> {
        const { initialTranslations } = options;

        this.initMoment();
        this.i18nextInstance = i18next.createInstance();
        this.initialTranslations = initialTranslations;
        await this.i18nextInstance.use(initReactI18next).init({
            // fallbackLng: 'en-GB', - we changed i18n strategy to fallbackNS change comment below.
            // the issue discussion https://github.com/i18next/i18next-gitbook/issues/49
            resources: {
                [DEFAULT_LANGUAGE]: {
                    [TRANSLATION_NAME_SPACES.TRANSLATIONS]: initialTranslations,
                    [TRANSLATION_NAME_SPACES.FALLBACK_TRANSLATIONS]: initialTranslations
                }
            },
            ns: [
                TRANSLATION_NAME_SPACES.TRANSLATIONS,
                TRANSLATION_NAME_SPACES.FALLBACK_TRANSLATIONS
            ],
            fallbackNS: [
                TRANSLATION_NAME_SPACES.TRANSLATIONS,
                TRANSLATION_NAME_SPACES.FALLBACK_TRANSLATIONS
            ],
            debug: false,
            react: {
                useSuspense: true,
                nsMode: 'fallback'
            },
            saveMissing: true,
            missingKeyHandler: (...args: any) => {
                /* Most of the time missing keys are either analytics attributes, or custom item names.
                  Shouldn't be displayed in production-like environments */
                if (options.env === ENV.DEV) {
                    console.warn('MISSING KEY', args);
                }
            }
        });
        this.i18nextInstance.changeLanguage(DEFAULT_LANGUAGE);
    }

    public async changeLanguage(options: IChangeLanguage): Promise<string> {
        const { languageCode, translationsPath, fallbackTranslationsPath, defaultTranslations = {} } = options;

        this.configureMoment(languageCode);

        const getTranslationsPromise = translationsPath
            ? fetchLanguage(translationsPath, this.useCache).then(response => response.data)
            : Promise.reject();

        const getFallbackTranslationsPromise: Promise<ITranslation> = (() => {
            // Caching the data, in order to re-use it as custom fallback for other languages
            // The custom fallback language is the same for any language
            if (this.fallbackTranslation) {
                return Promise.resolve(this.fallbackTranslation);
            } else if (!fallbackTranslationsPath) {
                return Promise.reject();
            } else {
                return fetchLanguage(fallbackTranslationsPath).then(response => {
                    this.fallbackTranslation = response.data;
                    return response.data;
                });
            }
        })();

        /*
         * This Promise will be resolved even if all Promises was rejected.
         * */
        return new Promise<string>((resolve, reject) => {
            Promise.all<IPromisedTranslation>([
                getTranslationsPromise.catch(() => {
                    console.warn(`Can't load translations from '${translationsPath}'`);
                }),
                getFallbackTranslationsPromise.catch(() => {
                    console.warn(`Can't load fallback translations from '${fallbackTranslationsPath}'`);
                })
            ]).then(
                ([translations, fallbackTranslations]: Array<IPromisedTranslation>): void => {
                    const translationLoadedSuccessful =
                        this.initialTranslations || translations || fallbackTranslations;

                    if (translationLoadedSuccessful) {
                        if (fallbackTranslations) {
                            this.i18nextInstance.addResourceBundle(
                                languageCode,
                                TRANSLATION_NAME_SPACES.FALLBACK_TRANSLATIONS,
                                {
                                    ...fallbackTranslations,
                                    ...defaultTranslations
                                }
                            );
                        }
                        if (translations) {
                            this.i18nextInstance.addResourceBundle(languageCode, TRANSLATION_NAME_SPACES.TRANSLATIONS, {
                                ...translations,
                                ...defaultTranslations
                            });
                        }
                        this.i18nextInstance.changeLanguage(languageCode);
                        resolve(languageCode);
                    } else {
                        reject();
                    }
                }
            );
        });
    }

    /**
     * {@link https://mdc-tomcat-jira35.ubisoft.org/jira/browse/GEN-1824}
     * Configures cache mode for translation's fetching:
     *  true (default) - fetch request fires only once for particular locale avoiding redundant downloads on production
     *  false - cache's not used; ideal for Preview Mode when translations can be periodically refreshed
     * @access public
     * @param {boolean} useCache @default true
     */
    public setCaching(useCache: boolean = true): void {
        this.useCache = useCache;
    }

    private configureMoment(locale: string = DEFAULT_LANGUAGE): void {
        moment.locale(locale);
    }

    private initMoment(): void {
        // Determine locale, because we have different rules for them.
        moment.defineLocale('es-MX', { parentLocale: 'es' });
        moment.defineLocale('es-ES', { parentLocale: 'es' });
    }

    public getAnalyticsLabel(key: string): string {
        const getAnalyticsLabel = this.i18nextInstance.getFixedT(DEFAULT_LANGUAGE);
        return getAnalyticsLabel(key);
    }
}

export const i18nService = new I18nService();

export default i18nService;
